import { Group, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InitiativeOption } from '../../../../store/process-analysis-store/process-analysis-store.type';
import { inkLight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { InitiativeItem } from './initiative-item';
import { MAX_SELECTED_INITIATIVES } from './initiative-list.constants';

type Props = {
  initiatives: InitiativeOption[];
  selectedInitiatives: InitiativeOption[];
  selectedTrends: InitiativeOption[];
  onToggleSelection: (initiative: InitiativeOption) => void;
  onToggleTrend: (initiative: InitiativeOption) => void;
  isLoading?: boolean;
};

export const InitiativeList = ({
  initiatives,
  selectedInitiatives,
  selectedTrends,
  onToggleSelection,
  onToggleTrend,
  isLoading = false,
}: Props) => {
  const i18n = useTranslation();
  const initiativeOptions = useMemo(() => {
    return initiatives
      .map((initiative) => ({
        id: initiative.id,
        name: initiative.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [initiatives]);

  const selectedIds = new Set(selectedInitiatives.map((item) => item.id));
  const selectedTrendIds = new Set(selectedTrends.map((item) => item.id));

  if (initiativeOptions.length === 0 && !isLoading) {
    return (
      <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
        {i18n.t('process_analysis.filters.no_initiatives_available')}
      </Text>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="small" weight="bold">
          {i18n.t('common.initiatives')}
        </Text>
        <Text size="small" weight="bold">
          {i18n.t('process_analysis.filters.trends')}
        </Text>
      </Group>
      {initiativeOptions.map((initiative) => (
        <InitiativeItem
          key={initiative.id}
          initiative={initiative}
          isSelected={selectedIds.has(initiative.id)}
          isSelectedTrends={selectedTrendIds.has(initiative.id)}
          isDisabled={!selectedIds.has(initiative.id) && selectedInitiatives.length >= MAX_SELECTED_INITIATIVES}
          onToggleSelection={() => onToggleSelection(initiative)}
          onToggleTrend={() => onToggleTrend(initiative)}
        />
      ))}
    </Stack>
  );
};
